import { nflTeamAbbrFromLabel } from "../utils/nflFranchises";
import { parseSeasonStatValue } from "../utils/playerSeasonChart";
import { formatPlayerName } from "../utils/textUtils";
import { buildSeasonChartSeries } from "../utils/seasonChartSeries";
import { compareYearKeys, sortYearKeys } from "../utils/yearKey";
import {
  NAME_SORT_ORDER,
  STAT_SORT_ORDER,
  sortedClassFor,
} from "../utils/resultsGrid";
import {
  classifySeason,
  nflPasserRating,
  parseStatNumber,
  seasonInScope,
  US_MAJOR_SCOPE,
} from "../utils/nflLeagueScope";

export const NFL_STAT_GROUPS = [
  { id: "passing", label: "Passing" },
  { id: "rushing", label: "Rushing" },
  { id: "receiving", label: "Receiving" },
];

const PASSING_FIELDS = [
  { id: "year", label: "Season", format: "season", width: 110 },
  { id: "team", label: "Tm", format: "text", width: 72 },
  { id: "att", label: "ATT", format: "count", width: 72 },
  { id: "com", label: "CMP", format: "count", width: 72 },
  { id: "yds", label: "YDS", format: "count", width: 80, chart: true },
  { id: "td", label: "TD", format: "count", width: 64, chart: true },
  { id: "int", label: "INT", format: "count", width: 64, chart: true },
  { id: "rtg", label: "RTG", format: "decimal1", width: 72, chart: true },
  { id: "sk", label: "SK", format: "count", width: 64 },
];

const RUSHING_FIELDS = [
  { id: "year", label: "Season", format: "season", width: 110 },
  { id: "team", label: "Tm", format: "text", width: 72 },
  { id: "att", label: "ATT", format: "count", width: 72 },
  { id: "yds", label: "YDS", format: "count", width: 80, chart: true },
  { id: "td", label: "TD", format: "count", width: 64, chart: true },
  { id: "y_att", label: "Y/A", format: "decimal1", width: 72 },
];

const RECEIVING_FIELDS = [
  { id: "year", label: "Season", format: "season", width: 110 },
  { id: "team", label: "Tm", format: "text", width: 72 },
  { id: "rec", label: "REC", format: "count", width: 72, chart: true },
  { id: "yds", label: "YDS", format: "count", width: 80, chart: true },
  { id: "td", label: "TD", format: "count", width: 64, chart: true },
];

const FIELDS_BY_GROUP = {
  passing: PASSING_FIELDS,
  rushing: RUSHING_FIELDS,
  receiving: RECEIVING_FIELDS,
};

const DEFAULT_CHART_STAT = {
  passing: "yds",
  rushing: "yds",
  receiving: "yds",
};

const COUNTING_FIELDS_BY_GROUP = {
  passing: ["att", "com", "yds", "td", "int", "sk"],
  rushing: ["att", "yds", "td"],
  receiving: ["rec", "yds", "td"],
};

export const NFL_COMPARE_PRESETS = [
  { label: "Brady vs Montana", players: ["brad06400", "mont00800"] },
  { label: "Rice vs Moss", players: ["rice01800", "moss01500"] },
];

const NFL_COMPARE_LOWER_IS_BETTER = new Set(["int", "sk"]);

const isCompareValueField = (field) =>
  field.id !== "year" && field.id !== "team";

export function getNflTeamAbbrFromSeasonLabel(label) {
  const text = String(label || "");
  const codes = [...text.matchAll(/\b([A-Z]{2,4})\s+(?:NFL|AFL|AAFC)\b/gi)]
    .map((match) => match[1].toUpperCase())
    .filter((code) => !["NFL", "AFL", "AAFC"].includes(code));
  if (codes.length) {
    return nflTeamAbbrFromLabel(codes[codes.length - 1]);
  }
  const named = text.match(/^\d{4}\s+(.+?)\s*\((?:NFL|AFL|AAFC)\)/i);
  if (named) return nflTeamAbbrFromLabel(named[1]);
  return "";
}

const NFL_TEAM_LOOKUP_CATEGORIES = [
  ...NFL_STAT_GROUPS.map((group) => group.id),
  "participation",
  "defense",
  "kicking",
  "punting",
  "returns",
];

export function resolveNflSeasonTeam(row = {}) {
  return (
    getNflTeamAbbrFromSeasonLabel(row.season) ||
    nflTeamAbbrFromLabel(row.team) ||
    nflTeamAbbrFromLabel(row.tm) ||
    getNflTeamAbbrFromSeasonLabel(row.year_team) ||
    ""
  );
}

export function resolveNflSeasonTeamForYear(player, year, row = {}) {
  const direct = resolveNflSeasonTeam(row);
  if (direct) return direct;
  for (const category of NFL_TEAM_LOOKUP_CATEGORIES) {
    const other = player?.stats?.[category]?.seasons?.[year];
    if (!other || other === row) continue;
    const team = resolveNflSeasonTeam(other);
    if (team) return team;
  }
  return "";
}

export function getNflCareerTeamAbbrs(player, leagueScope = US_MAJOR_SCOPE) {
  const years = new Set();
  NFL_TEAM_LOOKUP_CATEGORIES.forEach((category) => {
    Object.keys(player?.stats?.[category]?.seasons || {}).forEach((year) => {
      years.add(year);
    });
  });

  const teams = [];
  sortYearKeys([...years], "asc").forEach((year) => {
    NFL_TEAM_LOOKUP_CATEGORIES.forEach((category) => {
      const row = player?.stats?.[category]?.seasons?.[year];
      if (!row) return;
      const team = resolveNflSeasonTeam(row);
      if (!seasonInScope(row.season, team, leagueScope)) return;
      if (team && !teams.includes(team)) teams.push(team);
    });
  });
  return teams;
}

export function getNflCompareStats(groupId = "passing") {
  return getNflGridFields(groupId)
    .filter(isCompareValueField)
    .map((field) => ({
      id: field.id,
      label: field.label,
      group: groupId,
      format: field.format,
    }));
}

export const getNflPlayerPath = (playerId) =>
  playerId ? `/app/nfl/player/${playerId}` : "/app/nfl/players";

export function getNflDisplayName(player, fallback = "Unknown Player") {
  if (!player) return fallback;
  return formatPlayerName(player) || player.name || fallback;
}

export const getNflComparePath = (playerIds = []) => {
  const ids = playerIds.filter(Boolean);
  if (ids.length === 0) return "/app/nfl/compare";
  return `/app/nfl/compare?players=${ids.join(",")}`;
};

export function getNflGridFields(groupId) {
  return FIELDS_BY_GROUP[groupId] || PASSING_FIELDS;
}

export function getNflChartStats(groupId) {
  return getNflGridFields(groupId).filter((field) => field.chart);
}

export function getNflDefaultChartStat(groupId) {
  return DEFAULT_CHART_STAT[groupId] || DEFAULT_CHART_STAT.passing;
}

export function playerHasNflSeasons(player) {
  return NFL_STAT_GROUPS.some(
    (group) => Object.keys(getNflSeasonMap(player, group.id)).length > 0
  );
}

export function getNflSeasonMap(player, groupId) {
  return player?.stats?.[groupId]?.seasons || {};
}

export function getNflCareer(player, groupId) {
  return player?.stats?.[groupId]?.career || {};
}

function inScopeSeasons(player, groupId, leagueScope) {
  const seasonsMap = getNflSeasonMap(player, groupId);
  return Object.entries(seasonsMap).filter(([yearKey, row]) => {
    const resolvedTeam = resolveNflSeasonTeamForYear(player, yearKey, row || {});
    return seasonInScope((row || {}).season, resolvedTeam, leagueScope);
  });
}

export function getNflScopedCareer(player, groupId, leagueScope = US_MAJOR_SCOPE) {
  const countingFields = COUNTING_FIELDS_BY_GROUP[groupId] || COUNTING_FIELDS_BY_GROUP.passing;
  const counts = Object.fromEntries(countingFields.map((f) => [f, 0]));
  let seasonCount = 0;

  for (const [, row] of inScopeSeasons(player, groupId, leagueScope)) {
    seasonCount += 1;
    countingFields.forEach((field) => {
      const value = parseStatNumber(row?.[field]);
      if (value != null) counts[field] += value;
    });
  }

  const result = { ...counts, seasonCount };

  if (groupId === "passing") {
    result.rtg = nflPasserRating({
      att: counts.att,
      com: counts.com,
      yds: counts.yds,
      td: counts.td,
      int: counts.int,
    });
  }

  if (groupId === "rushing" && counts.att > 0) {
    result.y_att = counts.yds / counts.att;
  }

  return result;
}

export function getNflStatValue(row, fieldId) {
  if (!row || !fieldId) return null;
  if (fieldId === "team") {
    return resolveNflSeasonTeam(row) || null;
  }
  return parseSeasonStatValue(row[fieldId]);
}

export function formatNflStatValue(value, format = "count") {
  if (format === "season" || format === "text") {
    return value == null || value === "" ? "—" : String(value);
  }
  const num = parseSeasonStatValue(value);
  if (num == null) return "—";
  if (format === "decimal1") return num.toFixed(1);
  return Math.round(num).toLocaleString();
}

export function buildNflSeasonRows(player, groupId, leagueScope = US_MAJOR_SCOPE) {
  const seasons = getNflSeasonMap(player, groupId);
  const valueFields = getNflGridFields(groupId).filter(
    (field) => field.id !== "year" && field.id !== "team"
  );
  const scopedYears = inScopeSeasons(player, groupId, leagueScope).map(([yearKey]) => yearKey);

  return sortYearKeys(scopedYears, "desc").map((yearKey) => {
    const season = seasons[yearKey] || {};
    const row = {
      id: yearKey,
      year: yearKey,
      team: resolveNflSeasonTeamForYear(player, yearKey, season),
    };
    valueFields.forEach((field) => {
      row[field.id] = getNflStatValue(season, field.id);
    });
    return row;
  });
}

export function buildNflSeasonChartPoints(player, fieldId, groupId, leagueScope = US_MAJOR_SCOPE) {
  const resolvedGroup = groupId || "passing";
  const filteredSeasons = Object.fromEntries(inScopeSeasons(player, resolvedGroup, leagueScope));
  return buildSeasonChartSeries(filteredSeasons, (row) =>
    getNflStatValue(row, fieldId)
  );
}

export function getNflSeasonColumns(groupId, sortedField = "year") {
  return getNflGridFields(groupId).map((field) => {
    const isYear = field.id === "year";
    const isText = field.format === "text" || isYear;
    return {
      field: field.id,
      headerName: field.label,
      width: field.width,
      type: isText ? undefined : "number",
      align: isText ? "left" : "right",
      headerAlign: isText ? "left" : "right",
      sortingOrder: isYear ? STAT_SORT_ORDER : isText ? NAME_SORT_ORDER : STAT_SORT_ORDER,
      headerClassName: sortedClassFor(field.id, sortedField),
      cellClassName: sortedClassFor(field.id, sortedField),
      ...(isYear ? { sortComparator: compareYearKeys } : {}),
      valueFormatter: ({ value }) => formatNflStatValue(value, field.format),
    };
  });
}

export function getNflCompareCareerRowDefs(groupId = "passing") {
  const valueFields = getNflGridFields(groupId).filter(isCompareValueField);
  return [
    {
      id: "seasons",
      key: "seasons",
      label: "Seasons",
      format: "count",
      computed: "seasonCount",
    },
    ...valueFields.map((field) => ({
      id: field.id,
      key: field.id,
      label: field.label,
      format: field.format,
      group: groupId,
      lowerIsBetter: NFL_COMPARE_LOWER_IS_BETTER.has(field.id),
    })),
  ];
}

export function getNflCompareCareerValue(player, field, groupId = "passing", leagueScope = US_MAJOR_SCOPE) {
  const career = getNflScopedCareer(player, field?.group || groupId, leagueScope);
  if (field?.computed === "seasonCount" || field?.id === "seasons") {
    return career.seasonCount > 0 ? career.seasonCount : null;
  }
  return getNflStatValue(career, field.id);
}

export function getNflCompareStat(statId, groupId) {
  const resolvedGroup = groupId || "passing";
  const stats = getNflCompareStats(resolvedGroup);
  return (
    stats.find((stat) => stat.id === statId) ||
    stats.find((stat) => stat.id === getNflDefaultChartStat(resolvedGroup)) ||
    stats[0]
  );
}

export function buildNflCompareNamedSeries(players = [], fieldId, groupId, leagueScope = US_MAJOR_SCOPE) {
  return players.map((player) => ({
    name: getNflDisplayName(player),
    points: buildNflSeasonChartPoints(player, fieldId, groupId, leagueScope),
  }));
}

export function getNflCompareSubtitle(player, groupId = "passing", leagueScope = US_MAJOR_SCOPE) {
  const bio = player?.bio || player?.bioData || {};
  const parts = [];
  if (bio.position) parts.push(bio.position);

  const seasons = sortYearKeys(
    inScopeSeasons(player, groupId, leagueScope).map(([yearKey]) => yearKey)
  );
  if (seasons.length > 0) {
    const first = seasons[0];
    const last = seasons[seasons.length - 1];
    parts.push(first === last ? first : `${first}–${last}`);
  }

  return parts.length > 0 ? parts.join(" · ") : null;
}

export function hasNflCareerStats(player, groupId = "passing") {
  const career = getNflCareer(player, groupId);
  if (career && Object.keys(career).length > 0) return true;
  return Object.keys(getNflSeasonMap(player, groupId)).length > 0;
}

export function getNflAvailableStatGroups(player) {
  return NFL_STAT_GROUPS.filter((group) => hasNflCareerStats(player, group.id));
}

export function getNflDefaultStatGroup(player) {
  const position = String(
    player?.bio?.position || player?.bioData?.position || ""
  ).toUpperCase();
  const preferred = [];

  if (position.includes("QB")) preferred.push("passing");
  if (position.includes("RB") || position.includes("FB")) preferred.push("rushing");
  if (position.includes("WR") || position.includes("TE")) {
    preferred.push("receiving");
  }

  const candidates = [...new Set([...preferred, ...NFL_STAT_GROUPS.map((g) => g.id)])];
  return (
    candidates.find((groupId) => hasNflCareerStats(player, groupId)) || "passing"
  );
}

export const NFL_CAREER_HIGHLIGHT_FIELDS = {
  passing: ["yds", "td", "int", "rtg"],
  rushing: ["yds", "td", "att"],
  receiving: ["yds", "rec", "td"],
};

export function getNflCareerHighlightFields(groupId) {
  return NFL_CAREER_HIGHLIGHT_FIELDS[groupId] || NFL_CAREER_HIGHLIGHT_FIELDS.passing;
}

export function playerHasHiddenCflSeasons(player) {
  return NFL_STAT_GROUPS.some((group) => {
    const seasons = getNflSeasonMap(player, group.id);
    return Object.values(seasons).some(
      (row) => classifySeason((row || {}).season, (row || {}).team) === "cfl"
    );
  });
}
